import { useState, useRef, useMemo, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Video, Upload, Loader2, Sparkles, Plus, Minus, Trash2, Box, Lightbulb, X, Camera, ChevronDown, ChevronsDownUp, ChevronsUpDown, Check, Film, Sofa, Ruler } from "lucide-react";
import { toast } from "sonner";
import { SilentRecorder, canRecordInApp } from "@/components/SilentRecorder";

const MAX_BYTES = 100 * 1024 * 1024;
const MAX_SECONDS = 120;
const CHUNK = 4 * 1024 * 1024;
const PACKING = 1.15;

const probeDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

const STEPS = [
  { label: "Reading video", icon: Film, at: 0 },
  { label: "Spotting furniture", icon: Sofa, at: 10 },
  { label: "Calculating m³", icon: Ruler, at: 28 },
];

function AnalysisSteps() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const current = STEPS.reduce((c, st, i) => (secs >= st.at ? i : c), 0);
  return (
    <div className="space-y-3" data-testid="analysis-steps">
      <ol className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1">
        {STEPS.map((st, i) => {
          const done = i < current, active = i === current;
          const Icon = done ? Check : st.icon;
          return (
            <li key={st.label} className="flex items-center gap-1 sm:flex-1 min-w-0" data-testid={`analysis-step-${i}`} data-state={done ? "done" : active ? "active" : "pending"}>
              <span className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors duration-500 min-w-0 ${done ? "bg-emerald-50 border-emerald-200 text-emerald-700" : active ? "bg-kinetic-light border-kinetic/40 text-foreground" : "border-border text-muted-foreground"}`}>
                {active ? <Loader2 className="h-3.5 w-3.5 animate-spin text-kinetic shrink-0" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{st.label}</span>
              </span>
              {i < STEPS.length - 1 && <span className={`hidden sm:block h-px flex-1 transition-colors duration-500 ${done ? "bg-emerald-300" : "bg-border"}`} />}
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground" data-testid="analysis-elapsed">{secs}s elapsed · usually 20–40 seconds per room</p>
    </div>
  );
}

export const inventoryTotal = (rooms) => Math.round(rooms.reduce((s, r) => s + r.items.reduce((a, it) => a + it.quantity * it.unit_m3, 0), 0) * PACKING * 100) / 100;

function FlagToggle({ on, label, onClick, testid, color }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors ${on ? color : "border-border text-muted-foreground hover:border-foreground/40"}`}>{label}</button>
  );
}

function ItemRow({ item, ri, ii, onChange, onDelete }) {
  const set = (patch) => onChange(ri, ii, { ...item, ...patch });
  const id = `${ri}-${ii}`;
  return (
    <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_110px_220px_70px_32px] items-center gap-2 py-2 border-b border-border/60 last:border-0" data-testid={`inv-item-${id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate" data-testid={`inv-item-name-${id}`}>{item.item_name}</p>
        <p className="text-[11px] text-muted-foreground">{item.unit_m3} m³ each</p>
      </div>
      <div className="flex items-center gap-1 justify-end sm:justify-center">
        <button type="button" data-testid={`inv-qty-minus-${id}`} onClick={() => set({ quantity: Math.max(1, item.quantity - 1) })} className="h-7 w-7 rounded-md border border-border flex items-center justify-center hover:border-kinetic"><Minus className="h-3 w-3" /></button>
        <span className="w-7 text-center font-mono text-sm font-semibold" data-testid={`inv-qty-${id}`}>{item.quantity}</span>
        <button type="button" data-testid={`inv-qty-plus-${id}`} onClick={() => set({ quantity: item.quantity + 1 })} className="h-7 w-7 rounded-md border border-border flex items-center justify-center hover:border-kinetic"><Plus className="h-3 w-3" /></button>
      </div>
      <div className="flex gap-1.5 col-span-3 sm:col-span-1 order-4 sm:order-none">
        <FlagToggle on={item.is_fragile} label="Fragile" testid={`inv-fragile-${id}`} onClick={() => set({ is_fragile: !item.is_fragile })} color="bg-amber-50 border-amber-300 text-amber-700" />
        <FlagToggle on={item.is_heavy} label="Heavy" testid={`inv-heavy-${id}`} onClick={() => set({ is_heavy: !item.is_heavy })} color="bg-slate-800 border-slate-800 text-white" />
        <FlagToggle on={item.disassembly_required} label="Disassemble" testid={`inv-disassemble-${id}`} onClick={() => set({ disassembly_required: !item.disassembly_required })} color="bg-sky-50 border-sky-300 text-sky-700" />
      </div>
      <p className="font-mono text-sm text-right hidden sm:block" data-testid={`inv-vol-${id}`}>{(item.quantity * item.unit_m3).toFixed(2)}</p>
      <button type="button" data-testid={`inv-delete-${id}`} onClick={() => onDelete(ri, ii)} className="h-7 w-7 rounded-md text-muted-foreground hover:text-destructive flex items-center justify-center justify-self-end order-3 sm:order-none"><Trash2 className="h-3.5 w-3.5" /></button>
      <Input value={item.notes || ""} onChange={(e) => set({ notes: e.target.value })} maxLength={200} placeholder="Describe this item (e.g. glass top, oak, 2 parts)"
        className="h-8 text-xs col-span-3 sm:col-span-5 order-5 sm:order-none" data-testid={`inv-notes-${id}`} />
    </div>
  );
}

function AddItem({ ri, onAdd }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get("/inventory/volume", { params: { name } });
      onAdd(ri, { item_name: name.trim(), quantity: 1, is_fragile: false, is_heavy: false, disassembly_required: false, unit_m3: data.unit_m3 });
      setName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add that item");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 pt-3">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a missing item, e.g. Bookshelf" className="h-9 text-sm" data-testid={`inv-add-input-${ri}`}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
      <Button type="button" size="sm" variant="outline" onClick={add} disabled={busy || !name.trim()} data-testid={`inv-add-btn-${ri}`} className="gap-1"><Plus className="h-4 w-4" /> Add</Button>
    </div>
  );
}

function RoomVideoBar({ ri, roomVideo }) {
  if (roomVideo.busyRoom === ri) return <div className="pt-3">{roomVideo.busyNode}</div>;
  return (
    <div className="mt-3 rounded-lg border border-dashed border-kinetic/40 bg-kinetic-light/40 p-3 flex flex-wrap items-center gap-2" data-testid={`inv-room-video-bar-${ri}`}>
      <span className="text-xs text-muted-foreground mr-auto flex items-center gap-1.5"><Video className="h-3.5 w-3.5 text-kinetic" /> Film this room and AI adds what it sees</span>
      <Button type="button" size="sm" onClick={() => roomVideo.onRecord(ri)} disabled={roomVideo.busyRoom != null} data-testid={`inv-room-record-btn-${ri}`} className="gap-1 bg-kinetic hover:bg-kinetic-hover text-white"><Camera className="h-4 w-4" /> Take room video</Button>
      <Button type="button" size="sm" variant="outline" onClick={() => roomVideo.onUpload(ri)} disabled={roomVideo.busyRoom != null} data-testid={`inv-room-upload-btn-${ri}`} className="gap-1"><Upload className="h-4 w-4" /> Upload</Button>
    </div>
  );
}

export function InventoryReview({ rooms, onChange, readOnly = false, unload, roomVideo }) {
  const total = useMemo(() => inventoryTotal(rooms), [rooms]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (ri) => setCollapsed((s) => { const n = new Set(s); if (n.has(ri)) n.delete(ri); else n.add(ri); return n; });
  const allCollapsed = rooms.length > 0 && rooms.every((_, i) => collapsed.has(i));
  const count = rooms.reduce((s, r) => s + r.items.reduce((a, i) => a + i.quantity, 0), 0);
  const changeItem = (ri, ii, item) => onChange(rooms.map((r, i) => i !== ri ? r : { ...r, items: r.items.map((it, j) => (j === ii ? item : it)) }));
  const deleteItem = (ri, ii) => { setCollapsed(new Set()); onChange(rooms.map((r, i) => i !== ri ? r : { ...r, items: r.items.filter((_, j) => j !== ii) }).filter((r) => r.items.length)); };
  const addItem = (ri, item) => onChange(rooms.map((r, i) => (i !== ri ? r : { ...r, items: [...r.items, item] })));
  const addRoom = () => {
    setCollapsed(new Set(rooms.map((_, i) => i)));
    onChange([...rooms, { room_name: `Room ${rooms.length + 1}`, items: [{ item_name: "Box", quantity: 1, is_fragile: false, is_heavy: false, disassembly_required: false, unit_m3: 0.1 }] }]);
  };

  return (
    <div className="space-y-4" data-testid="inventory-review">
      <Card className="p-5 bg-asphalt text-white border-asphalt flex items-center justify-between gap-4" data-testid="inventory-total-card">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-white/60">Total estimated volume</p>
          <p className="font-display text-4xl font-bold leading-tight"><span data-testid="inventory-total-m3">{total.toFixed(2)}</span> <span className="text-xl text-white/70">m³</span></p>
          <p className="text-xs text-white/60 mt-1">{count} items · includes 15% packing buffer</p>
        </div>
        <Box className="h-12 w-12 text-kinetic" />
      </Card>
      {rooms.length > 1 && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(rooms.map((_, i) => i)))} data-testid="inv-toggle-all-btn"
            className="text-xs font-medium text-muted-foreground hover:text-kinetic flex items-center gap-1">
            {allCollapsed ? <><ChevronsUpDown className="h-3.5 w-3.5" /> Expand all rooms</> : <><ChevronsDownUp className="h-3.5 w-3.5" /> Collapse all rooms</>}
          </button>
        </div>
      )}
      {rooms.map((room, ri) => {
        const open = !collapsed.has(ri);
        return (
        <Card key={ri} className="p-4" data-testid={`inv-room-${ri}`}>
          <div className={`flex items-center justify-between gap-2 ${open ? "mb-1" : ""}`}>
            <button type="button" onClick={() => toggle(ri)} aria-expanded={open} data-testid={`inv-room-toggle-${ri}`}
              className="h-7 w-7 shrink-0 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground">
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
            </button>
            {readOnly ? <h4 className="font-display font-semibold mr-auto cursor-pointer" onClick={() => toggle(ri)}>{room.room_name}</h4>
              : <Input value={room.room_name} onChange={(e) => onChange(rooms.map((r, i) => (i === ri ? { ...r, room_name: e.target.value } : r)))} className="h-8 font-display font-semibold border-transparent hover:border-border px-1 min-w-0 flex-1 sm:flex-none sm:w-56 mr-auto" data-testid={`inv-room-name-${ri}`} />}
            <span className="font-mono text-xs text-muted-foreground text-right shrink-0" data-testid={`inv-room-summary-${ri}`}>
              {!open && (() => { const n = room.items.reduce((a, i) => a + i.quantity, 0); return <span className="mr-2 hidden sm:inline">{n} {n === 1 ? "item" : "items"} ·</span>; })()}
              {room.items.reduce((a, i) => a + i.quantity * i.unit_m3, 0).toFixed(2)} m³
            </span>
          </div>
          {open && <>
          {readOnly && unload?.[room.room_name] && <p className="text-xs text-kinetic font-medium mb-1" data-testid={`inv-room-unload-${ri}`}>Unload at: {unload[room.room_name]}</p>}
          <div className="hidden sm:grid grid-cols-[1fr_110px_220px_70px_32px] gap-2 text-[10px] font-mono uppercase text-muted-foreground pb-1 border-b border-border">
            <span>Item</span><span className="text-center">Qty</span><span>Flags</span><span className="text-right">m³</span><span />
          </div>
          {room.items.map((it, ii) => readOnly ? (
            <div key={ii} className="flex items-center justify-between py-2 border-b border-border/60 last:border-0 text-sm" data-testid={`inv-ro-item-${ri}-${ii}`}>
              <span className="truncate"><span className="font-mono font-semibold mr-2">{it.quantity}×</span>{it.item_name}
                {it.is_fragile && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Fragile</span>}
                {it.is_heavy && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-white">Heavy</span>}
                {it.disassembly_required && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">Disassemble</span>}
                {it.notes && <span className="block text-xs text-muted-foreground mt-0.5 whitespace-normal" data-testid={`inv-ro-notes-${ri}-${ii}`}>{it.notes}</span>}
              </span>
              <span className="font-mono text-muted-foreground shrink-0">{(it.quantity * it.unit_m3).toFixed(2)}</span>
            </div>
          ) : <ItemRow key={ii} item={it} ri={ri} ii={ii} onChange={changeItem} onDelete={deleteItem} />)}
          {!readOnly && <AddItem ri={ri} onAdd={addItem} />}
          {!readOnly && roomVideo && <RoomVideoBar ri={ri} roomVideo={roomVideo} />}
          </>}
        </Card>
        );
      })}
      {!readOnly && <Button type="button" variant="outline" size="sm" onClick={addRoom} data-testid="inv-add-room-btn" className="gap-1"><Plus className="h-4 w-4" /> Add room</Button>}
    </div>
  );
}

const ROOM_PRESETS = ["Living room", "Kitchen", "Dining room", "Master bedroom", "Bedroom 2", "Bathroom", "Laundry", "Garage", "Study", "Outdoor"];
const normName = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/(es|s)$/, "");
const MAX_VIDEOS = 12;

const mergeRooms = (existing, incoming, path) => {
  const names = new Set(existing.map((r) => r.room_name.toLowerCase()));
  const added = incoming.map((r) => {
    let n = r.room_name; let k = 2;
    while (names.has(n.toLowerCase())) n = `${r.room_name} ${k++}`;
    names.add(n.toLowerCase());
    return { ...r, room_name: n, source_video: path, items: r.items.map((it) => ({ ...it, source_video: path })) };
  });
  return [...existing, ...added];
};

function DupRoomPrompt({ name, choice, setChoice }) {
  const opt = (v, text) => (
    <button type="button" onClick={() => setChoice(v)} data-testid={`dup-room-${v}-btn`}
      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${choice === v ? "bg-asphalt text-white border-asphalt" : "border-border bg-white hover:border-kinetic"}`}>{text}</button>
  );
  return (
    <div className="rounded-lg border border-kinetic/40 bg-kinetic-light/40 p-3 space-y-2" data-testid="dup-room-prompt">
      <p className="text-sm font-medium">You already have a room called "{name}". Add to the existing {name} or create a new room?</p>
      <div className="flex flex-wrap gap-2">{opt("add", `Add to existing ${name}`)}{opt("new", "Create a new room")}</div>
    </div>
  );
}

function CapturePanel({ roomName, setRoomName, onRecord, onUpload, onCancel, isFirst, dupName, choice, setChoice }) {
  const blocked = !!dupName && !choice;
  return (
    <div className="space-y-4" data-testid="video-capture-widget">
      <div className="rounded-lg border border-dashed border-kinetic/40 bg-kinetic-light/40 p-4 flex gap-3">
        <Lightbulb className="h-5 w-5 text-kinetic shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Tips for the best estimate</p>
          <p className="text-muted-foreground">Record one short video per room. Walk slowly around the room, keep lights on, pan across all furniture. Max 2 minutes / 100MB each.</p>
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Which room is this video of? <span className="text-muted-foreground font-normal">(optional — AI will guess if left blank)</span></p>
        <Input value={roomName} onChange={(e) => setRoomName(e.target.value)} placeholder="e.g. Master bedroom" className="h-9 text-sm max-w-xs" data-testid="video-room-name-input" maxLength={60} />
        <div className="flex flex-wrap gap-1.5">
          {ROOM_PRESETS.map((p) => (
            <button key={p} type="button" onClick={() => setRoomName(p)} data-testid={`room-preset-${p.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${roomName === p ? "bg-asphalt text-white border-asphalt" : "border-border hover:border-kinetic"}`}>{p}</button>
          ))}
        </div>
      </div>
      {dupName && <DupRoomPrompt name={dupName} choice={choice} setChoice={setChoice} />}
      <div className={`grid sm:grid-cols-2 gap-3 ${blocked ? "opacity-50 pointer-events-none" : ""}`} aria-disabled={blocked}>
        <button type="button" onClick={onRecord} disabled={blocked} data-testid="record-video-btn" className="rounded-xl border-2 border-dashed border-border hover:border-kinetic transition-colors p-6 flex flex-col items-center gap-2 text-center">
          <Camera className="h-7 w-7 text-kinetic" /><span className="font-medium text-sm">Record {isFirst ? "a room" : "another room"}</span><span className="text-xs text-muted-foreground">Opens your camera on mobile</span>
        </button>
        <button type="button" onClick={onUpload} disabled={blocked} data-testid="upload-video-btn" className="rounded-xl border-2 border-dashed border-border hover:border-kinetic transition-colors p-6 flex flex-col items-center gap-2 text-center">
          <Video className="h-7 w-7 text-asphalt" /><span className="font-medium text-sm">Upload a room video</span><span className="text-xs text-muted-foreground">MP4, MOV or WebM</span>
        </button>
      </div>
      {onCancel && <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="video-add-cancel-btn" className="gap-1 text-muted-foreground"><X className="h-4 w-4" /> Cancel</Button>}
    </div>
  );
}

function VideoList({ videos, onRemove, onAdd, canAdd }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="room-video-list">
      {videos.map((v, i) => (
        <span key={v.path} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-kinetic-light text-sm border border-kinetic/30" data-testid={`room-video-chip-${i}`}>
          <Video className="h-3.5 w-3.5 text-kinetic" />{v.room_name || `Video ${i + 1}`}
          <button type="button" onClick={() => onRemove(v.path)} data-testid={`room-video-remove-${i}`} className="h-5 w-5 rounded-full hover:bg-white flex items-center justify-center text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
        </span>
      ))}
      {canAdd && <Button type="button" size="sm" variant="outline" onClick={onAdd} data-testid="add-room-video-btn" className="gap-1 rounded-full"><Plus className="h-4 w-4" /> Add another room video</Button>}
    </div>
  );
}

export function VideoInventory({ value, onChange }) {
  const [phase, setPhase] = useState("idle");
  const [adding, setAdding] = useState(false);
  const [roomName, setRoomName] = useState("");
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const targetRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [targetRoom, setTargetRoom] = useState(null);
  const [dupChoice, setDupChoice] = useState(null);
  const changeRoomName = (n) => { setRoomName(n); setDupChoice(null); };
  const dupIdx = roomName.trim() ? (value?.rooms || []).findIndex((r) => r.room_name.trim().toLowerCase() === roomName.trim().toLowerCase()) : -1;
  const startCapture = (ref) => { if (dupIdx >= 0 && dupChoice === "add") pickForRoom(dupIdx, ref); else openPicker(ref); };
  const pickForRoom = (ri, ref) => { targetRef.current = ri; openPicker(ref); };
  const [recorderOpen, setRecorderOpen] = useState(false);
  const openPicker = (ref) => { if (ref === camRef && canRecordInApp()) setRecorderOpen(true); else ref.current?.click(); };
  const videos = value?.videos || (value?.video_path ? [{ path: value.video_path, room_name: "Walkthrough" }] : []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    processFile(file);
  };

  const processFile = async (file) => {
    const target = targetRef.current;
    targetRef.current = null;
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("Video must be under 100MB"); return; }
    const dur = await probeDuration(file);
    if (dur > MAX_SECONDS + 2) { toast.error("Video must be 2 minutes or shorter"); return; }
    const targetName = target != null ? value?.rooms?.[target]?.room_name : null;
    if (targetName) setRoomName(targetName);
    setTargetRoom(target);
    setFileName(file.name); setPhase("uploading"); setProgress(0);
    const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk.bin");
        await api.post(`/inventory/upload-chunk?upload_id=${uploadId}&index=${i}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      setPhase("processing");
      const existingItems = targetName ? (valueRef.current?.rooms?.find((r) => r.room_name === targetName)?.items || []).map((it) => it.item_name) : [];
      const { data } = await api.post("/inventory/analyze-video", { upload_id: uploadId, filename: file.name || "video.mp4", total_chunks: total, room_name: (targetName || roomName).trim(), existing_items: existingItems.slice(0, 100) });
      const latest = valueRef.current;
      const prev = latest?.rooms || [];
      const curVideos = latest?.videos || videos;
      let rooms, label;
      const ti = targetName ? (prev[target]?.room_name === targetName ? target : prev.findIndex((r) => r.room_name === targetName)) : -1;
      if (targetName && ti >= 0) {
        const found = data.rooms.flatMap((r) => r.items);
        rooms = prev.map((r, i) => {
          if (i !== ti) return r;
          const items = r.items.map((it) => ({ ...it }));
          for (const it of found) {
            const same = items.find((x) => normName(x.item_name) === normName(it.item_name));
            if (same) same.quantity = Math.max(same.quantity, it.quantity);
            else items.push({ ...it, source_video: data.video_path });
          }
          return { ...r, items };
        });
        label = targetName;
        const dupes = curVideos.filter((v) => v.room_name === label || v.room_name.startsWith(`${label} (video `)).length;
        if (dupes) label = `${label} (video ${dupes + 1})`;
      } else {
        rooms = mergeRooms(prev, data.rooms, data.video_path);
        label = rooms.slice(prev.length).map((r) => r.room_name).join(", ") || roomName.trim() || `Room video ${curVideos.length + 1}`;
      }
      const nextVideos = [...curVideos, { path: data.video_path, room_name: label }];
      onChange({ ...(latest || {}), rooms, videos: nextVideos, video_path: nextVideos[0].path });
      setRoomName(""); setDupChoice(null); setAdding(false);
      toast.success(`${label}: found ${data.rooms.reduce((s, r) => s + r.items.length, 0)} item types — merged into your inventory`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Analysis failed");
    } finally {
      setPhase("idle"); setTargetRoom(null);
    }
  };

  const reset = () => { onChange(null); setAdding(false); };
  const removeVideo = (path) => {
    const rooms = value.rooms.map((r) => {
      const tagged = r.items.some((it) => it.source_video === path);
      if (!tagged && r.source_video === path) return null;
      const items = r.items.filter((it) => it.source_video !== path);
      if (!items.length) return null;
      const { source_video, ...rest } = r;
      return source_video === path ? { ...rest, items } : { ...r, items };
    }).filter(Boolean);
    const rest = videos.filter((v) => v.path !== path);
    if (!rooms.length && !rest.length) { reset(); return; }
    onChange({ ...value, rooms, videos: rest, video_path: rest[0]?.path || null });
  };
  const inputs = (
    <>
      <input ref={camRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={handleFile} data-testid="video-camera-input" />
      <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/*" className="hidden" onChange={handleFile} data-testid="video-file-input" />
      <SilentRecorder open={recorderOpen} maxSeconds={MAX_SECONDS}
        onDone={(f) => { setRecorderOpen(false); processFile(f); }} onClose={() => { setRecorderOpen(false); targetRef.current = null; }} />
    </>
  );

  const busy = phase === "uploading" || phase === "processing";
  const processingCard = busy && (
      <Card className="p-6 space-y-4" data-testid="video-processing-state">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-kinetic-light flex items-center justify-center">{phase === "uploading" ? <Upload className="h-5 w-5 text-kinetic" /> : <Sparkles className="h-5 w-5 text-kinetic animate-pulse" />}</div>
          <div className="flex-1">
            <p className="font-medium text-sm">{phase === "uploading" ? `Uploading ${fileName}…` : `AI is analyzing ${roomName.trim() || "your room"} and calculating cubic volume…`}</p>
            {phase === "uploading" && <p className="text-xs text-muted-foreground">{progress}%</p>}
          </div>
        </div>
        {phase === "uploading" ? <Progress value={progress} /> : <AnalysisSteps />}
      </Card>
  );

  const capture = <CapturePanel roomName={roomName} setRoomName={changeRoomName} isFirst={!videos.length}
    dupName={dupIdx >= 0 ? value.rooms[dupIdx].room_name : null} choice={dupChoice} setChoice={setDupChoice}
    onRecord={() => startCapture(camRef)} onUpload={() => startCapture(fileRef)} onCancel={value ? () => setAdding(false) : null} />;

  if (!value) return (
    <>
      {inputs}
      {busy ? processingCard : capture}
      {!busy && (
        <Button type="button" variant="link" className="px-0 mt-2 text-kinetic" data-testid="inv-manual-start-btn"
          onClick={() => onChange({ rooms: [{ room_name: "Room 1", items: [{ item_name: "Box", quantity: 1, is_fragile: false, is_heavy: false, disassembly_required: false, notes: "", unit_m3: 0.1 }] }], videos: [], video_path: null })}>
          No video? Add rooms manually instead
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      {inputs}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-kinetic" /> Review the merged inventory — adjust quantities or flags, add anything missed.</p>
        <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="video-reset-btn" className="gap-1 text-muted-foreground shrink-0"><X className="h-4 w-4" /> Start over</Button>
      </div>
      <VideoList videos={videos} onRemove={removeVideo} onAdd={() => setAdding(true)} canAdd={!adding && !busy && videos.length < MAX_VIDEOS} />
      {busy && targetRoom == null ? processingCard : adding && !busy && <Card className="p-4">{capture}</Card>}
      <InventoryReview rooms={value.rooms} onChange={(rooms) => onChange({ ...value, rooms })}
        roomVideo={{ onRecord: (ri) => pickForRoom(ri, camRef), onUpload: (ri) => pickForRoom(ri, fileRef), busyRoom: busy ? targetRoom ?? -1 : null, busyNode: processingCard }} />
    </div>
  );
}
